/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext, useEffect, useState } from 'react';
import Select from '@mui/material/Select';
import { CircularProgress, styled } from '@mui/material';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import EntriesRow from 'components/Monitor/ResultsTab/TableFilters/EntriesRow';
import { GlobalContext } from 'providers/GlobalProvider';
import { enqueueSnackbar } from 'notistack';
import BiteDBAPI from 'api/BiteDBAPI';
import EntityServiceAPI from 'api/EntityServiceAPI';
import { IDecompressedData, IScreeningResultRow } from 'interfaces/IBiteDB';
import { decompressData, formatFriendlyTime } from 'utils/common';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { Link } from 'react-router-dom';

interface IScreen {
  databases?: any;
  isLoading?: boolean;
  screeningResults: IScreeningResultRow[];
  onViewAllHistory: () => void;
}

const CustomSelect = styled(Select)({
  '.MuiSelect-icon': {
    color: 'var(--card-text)',
  },
  'overflow': 'hidden',
  'color': 'var(--Text-Main, var(--text-primary))',
  'textOverflow': 'ellipsis',
  'fontFamily': 'Inter',
  'fontSize': '16px',
  'fontStyle': 'normal',
  'fontWeight': 400,
  'lineHeight': '24px',
  'borderRadius': '8px',
  'border': '1px solid var(--bg-secondary)',
});

const RunScreening: React.FC<IScreen> = ({
  databases,
  isLoading,
  screeningResults,
  onViewAllHistory,
}) => {
  const { cur_user } = useContext(GlobalContext);
  const [ frequency, setFrequency ] = useState<string>('Weekly');
  const [ selectedOpts, setSelectedOpts ] = useState([]);
  const [ selectedRows, setSelectedRows ] = useState<any[]>([]);
  const [ allResults, setAllResults ] = React.useState<IDecompressedData[]>([]);
  const [ fieldTicketMap, setFieldTicketMap ] = React.useState<
    Record<string, string>
  >({});
  const [ fieldDatabaseMap, setFieldDatabaseMap ] = React.useState<
    Record<string, string>
  >({});

  useEffect(() => {
    if (screeningResults && screeningResults.length > 0) {
      const fieldToTicket: Record<string, string> = {};
      const fieldToDatabase: Record<string, string> = {};
      const aggregatedResults: IDecompressedData[] = [];

      screeningResults.forEach((row) => {
        const results = row.screening_result?.results;
        if (Array.isArray(results)) {
          results.forEach((r) => {
            if (r.allResults) {
              const decompressed = decompressData(
                r.allResults,
              ) as IDecompressedData[];
              decompressed.forEach((d) => {
                aggregatedResults.push(d);
                fieldToTicket[d.field] = row.screening_result.ticket_name;
                fieldToDatabase[d.field] = row.database_id;
                fieldToDatabase[d.field] = row.database_id;
              });
            }
          });
        }
      });

      setAllResults(aggregatedResults);
      setFieldTicketMap(fieldToTicket);
      setFieldDatabaseMap(fieldToDatabase);
      setFieldDatabaseMap(fieldToDatabase);
    }
  }, [ screeningResults ]);

  const handleFrequencyChange = (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    setFrequency(event.target.value as string);
  };
  const runScreening = async() => {
    const selected_team_id = cur_user?.selected_team?.id;
    if (!selected_team_id) {
      enqueueSnackbar('Please select a team', { variant: 'error' });
      return;
    }

    // Entity databases screen through the entity service; product/other databases
    // continue through the legacy automations-backed BiteDB endpoint.
    const entityIds = selectedRows
      .filter((db) => db.type === 'people & companies')
      .map((db) => db.id);
    const otherIds = selectedRows
      .filter((db) => db.type !== 'people & companies')
      .map((db) => db.id);

    const responses = await Promise.all([
      entityIds.length
        ? EntityServiceAPI.runDatabaseScreening({
          databaseIds: entityIds,
          screenedBy: cur_user?.basics?.id,
        })
        : null,
      otherIds.length ? BiteDBAPI.runDatabaseScreening(otherIds) : null,
    ]);

    if (responses.some((response) => response?.success)) {
      enqueueSnackbar('Successfully started a new screening', {
        variant: 'success',
      });
    }
  };

  return (
    <>
      <div
        className='api-container'
        style={{ gap: '10px', padding: '15px 0px 15px 0px', width: '100%' }}
      >
        <div className='api-container-left' style={{ flex: '1' }}>
          <div className='screening-card'>
            <div className='screening-card-header'>
              <div className='title-group'>
                {/* <span className='warning-icon'>⚠️</span> */}
                <h3>Run Immediate Screening</h3>
              </div>
            </div>
            <p className='subtitle'>
              Screen your databases against our dataset to identify potential
              risks
            </p>
            <div className='entries-frame-body-cont-lenses-col'>
              {isLoading ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                  }}
                >
                  <CircularProgress color='primary' />
                </div>
              ) : databases?.length === 0 ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                  }}
                >
                  <p style={{ color: 'var(--Primary-400, #4895ef)' }}>
                    No databases found
                  </p>
                </div>
              ) : (
                databases?.map((row: any, index: number) => (
                  <EntriesRow
                    key={index}
                    name={row.name}
                    row={row}
                    selectedItems={selectedOpts}
                    setSelectedItems={setSelectedOpts}
                    setSelectedRows={setSelectedRows}
                  />
                ))
              )}
            </div>
            <div className='team-content-header'>
              <div
                className='create-new-team-btn'
                style={{
                  width: '100%',
                  margin: '0px',
                  border: '1px solid var(--Primary-400, #4895ef)',
                  background: 'var(--Primary-400, #4895ef)',
                }}
                onClick={runScreening}
              >
                <div
                  className='create-new-team-btn-text'
                  style={{ color: 'var(--text-primary)' }}
                >
                  Run Screening Now
                </div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ flex: '1' }}>
          <div className='screening-card'>
            <div className='screening-card-header'>
              <div className='title-group'>
                {/* <span className='warning-icon'>⚠️</span> */}
                <h3>Recent Screening Results</h3>
              </div>
            </div>
            <p className='subtitle'>View your most recent screening results</p>
            <div style={{ height: '75%', maxHeight: '75%', overflowY: 'auto' }}>
              {allResults && allResults.length > 0 ? (
                allResults.slice(0, 4).map((item, index) => {
                  const ticketName = fieldTicketMap[item.field] ?? item.dataSet;
                  const id = fieldDatabaseMap[item.field];

                  return (
                    <div key={index} className='hit-item'>
                      <div className='hit-main'>
                        <div className='hit-title'>
                          <strong>{item.field}</strong>
                        </div>
                        <div className='hit-meta'>
                          {ticketName} •{' '}
                          <CalendarMonthOutlinedIcon
                            sx={{ fontSize: 14, color: 'var(--text-primary)' }}
                          />{' '}
                          {formatFriendlyTime(
                            screeningResults?.[0]?.screening_timestamp ??
                              undefined,
                          )}
                        </div>
                      </div>
                      <Link
                        to={{
                          pathname: `/database/${id}`,
                        }}
                      >
                        <div className='arrow'>›</div>
                      </Link>
                    </div>
                  );
                })
              ) : (
                <div
                  className='entries-frame-body-cont-lenses-col'
                  style={{ justifyContent: 'center' }}
                >
                  <NotificationsActiveOutlinedIcon sx={{ fontSize: 36 }} />
                  <p className='subtitle'>No recent screening results</p>
                  <p className='subtitle'>
                    Run a screening to see results here
                  </p>
                </div>
              )}
            </div>

            <div className='team-content-header'>
              <div
                className='create-new-team-btn'
                style={{ width: '100%', margin: '0px' }}
                onClick={onViewAllHistory}
              >
                <div
                  className='create-new-team-btn-text'
                  style={{ color: 'var(--text-primary)' }}
                >
                  View All History
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RunScreening;
